import React, { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { shallowEqual, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { useParams } from 'react-router-dom'
import { animateScroll } from 'react-scroll'
import block from 'bem-cn'

import useInfiniteVerticalScroll from 'shared/hooks/useInfiniteVerticalScroll'
import { localeCommonKeysType, localeSlotsKeysType } from 'shared/locale/en-US'

import { useFilterSlots } from 'modules/SlotsRoot/hooks'
import { IFiltersSlots } from 'modules/SlotsRoot/types/filters'
import { ISlotsGameType, ISlotViewItem, SlotsGameType } from 'modules/SlotsRoot/types/interfaces/Slots/Slots'

import { selectIsUserAuthenticated } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'
import { selectFavouriteList, selectGameList, selectGameListLoading } from 'features/slots/selectors'

import CarouselSlider from 'components/CarouselSlider/CarouselSlider'
import Spinner from 'components/Spinner'

import { mediaSize } from '../../shared/style/var'
import { EmptyFavorites } from './EmptyFavorites/EmptyFavorites'
import SlotsIcon from './SlotsIcon/SlotsIcon'

import './SlotsLayoutContent.scss'

const FilterBlock = React.lazy(() => import('./FilterBlock/FilterBlock'))
const NavigationMenu = React.lazy(() => import('./SlotsPreview/components/NavigationMenu/NavigationMenu'))

interface ISlotsLayoutContentProps {
  filters: IFiltersSlots
}

const slotsPerRender = 20
const b = block('slots-layout-content')

const SlotsLayoutContent: React.FC<ISlotsLayoutContentProps> = ({ filters }) => {
  const { selectedProviders, selectedCategories } = filters

  const locale = useSelector(selectLocaleDict, shallowEqual).slots
  const gameList = useSelector(selectGameList, shallowEqual)
  const favoritesList = useSelector(selectFavouriteList, shallowEqual)
  const gameListLoading = useSelector(selectGameListLoading, shallowEqual)

  const { gameType }: { gameType: ISlotsGameType } = useParams()

  useEffect(() => {
    animateScroll.scrollToTop()
  }, [gameType])

  const { slots, providers, categories } = useFilterSlots({
    filters,
    slots: gameList,
    gameType,
  })

  const filterCategories = useMemo(() => ['all', 'favorite', ...categories], [categories])

  const filteredListProviders = useMemo(
    () => slots.filter(t => !selectedProviders[0] || t.provider === selectedProviders[0]),
    [slots, selectedProviders],
  )

  const filteredSlots = useMemo(() => {
    if (selectedCategories.indexOf('favorite') !== -1) {
      return filteredListProviders.filter(slot => favoritesList.indexOf(`${slot.gameId}_${slot.provider}`) !== -1)
    }
    return filteredListProviders
  }, [filteredListProviders, favoritesList, selectedCategories]) // исключение для фаворитов (оптимизация)

  const { current, ref }: { current: ISlotViewItem[]; ref: any } = useInfiniteVerticalScroll({
    data: filteredSlots,
    loadingCount: slotsPerRender,
  })

  const isLiveCasino = useMemo(() => gameType === 'live-casino', [gameType])

  // const { getGameInfo } = usePragmaticDGAInfo({ slots: filteredSlots, needConnection: isLiveCasino })

  const currentSlots = useMemo(
    () =>
      current?.map(slot => (
        /* const gameInfo = getGameInfo(slot.gameId)
        if (gameInfo) {
          return <PragmaticDGA key={`${slot.gameId}_${slot.provider}`} slot={slot} gameInfo={gameInfo} />
        } */
        <SlotsIcon key={`${slot.gameId}_${slot.provider}`} slot={slot} isLive={isLiveCasino} />
      )) ?? [],
    [current, isLiveCasino],
  )

  return (
    <section className={b()}>
      <Helmet>
        <title>{locale[gameType as localeSlotsKeysType] || gameType}</title>
      </Helmet>
      <Spinner isLoading={gameListLoading} />

      <div className={b('content')}>
        <div className={b('top', { isLive: isLiveCasino })}>
          <div className={b('heading')}>
            <span className={b('game-type')}>{isLiveCasino ? locale.liveCasino : locale.casino}</span>
            <div className={b('count')}>
              <span>{filteredSlots.length}</span>
            </div>
          </div>

          <div className={b('navigation-block')}>
            <NavigationMenu />
          </div>

          <FilterBlock
            locale={locale}
            providers={providers}
            categories={filterCategories}
            filters={filters}
            isLive={isLiveCasino}
          />
        </div>

        <div className={b('slots-wrapper')}>
          <div className={b('slots', { isLive: isLiveCasino })}>
            <>{selectedCategories.includes('favorite') && !filteredSlots.length ? <EmptyFavorites /> : currentSlots}</>
          </div>
        </div>

        {current.length !== filteredSlots.length && filteredSlots.length && filteredSlots.length > slotsPerRender && (
          <div ref={ref} className={b('loading')}>
            <span>{locale.loading}</span>
          </div>
        )}
      </div>
    </section>
  )
}

export default SlotsLayoutContent
